import { IntFolder } from '@gen2/api/integrations/api';
import { AlertColor } from '@mui/material';
import { noop } from 'lodash';
import { create } from 'zustand';

type AlertSettings = {
  severity: AlertColor;
  message: string;
  open: boolean;
};

export type Site = {
  id: string;
  name: string;
  path: string;
  isOpen: boolean;
};

export type DocumentLibrary = {
  id: string;
  name: string;
  path: string;
  isOpen: boolean;
};

export interface IIntegrationsStore {
  alert: AlertSettings;
  selectedFolder: IntFolder | null;
  currentFolder: IntFolder;
  isFinderOpen: boolean;
  isCreateFolderOpen: boolean;
  isConnectConfirmOpen: boolean;
  isResyncOpen: boolean;
  connectConfirmTitle: string;
  setAlert: (alert: AlertSettings) => void;
  setSelectedFolder: (folder: IntFolder | null) => void;
  setCurrentFolder: (folder: IntFolder) => void;
  setFinderOpen: (isOpen: boolean) => void;
  setCreateFolderOpen: (isOpen: boolean) => void;

  confirmConnect: () => void;
  cancelConnect: () => void;

  setConnectConfirmOpen: (title: string) => Promise<boolean>;

  site: Site;
  documentLibrary: DocumentLibrary;

  setSitePickerOpen: () => Promise<Site | null>;
  setDocumentLibraryPickerOpen: () => Promise<DocumentLibrary | null>;

  confirmSite: (site: Site) => void;
  cancelSite: () => void;
  changeSite: (site: Site) => void;

  confirmDocumentLibrary: (library: DocumentLibrary) => void;
  cancelDocumentLibrary: () => void;
  changeDocumentLibrary: (library: DocumentLibrary) => void;

  confirmResync: () => void;
  cancelResync: () => void;
  setResyncOpen: () => Promise<boolean>;
}

export const useIntegrationsStore = create<IIntegrationsStore>()((set) => ({
  alert: {
    severity: 'success',
    message: '',
    open: false,
  },
  selectedFolder: null,
  currentFolder: {
    id: '',
    name: '',
    path: '',
  },
  isFinderOpen: false,
  isCreateFolderOpen: false,
  isConnectConfirmOpen: false,
  isResyncOpen: false,
  connectConfirmTitle: '',
  site: {
    id: '',
    name: '',
    path: '',
    isOpen: false,
  },

  documentLibrary: {
    id: '',
    name: '',
    path: '',
    isOpen: false,
  },

  setAlert: (alert) => set({ alert }),
  setSelectedFolder: (selectedFolder) => set({ selectedFolder }),
  setCurrentFolder: (currentFolder) => set({ currentFolder }),
  setFinderOpen: (isOpen) => set({ isFinderOpen: isOpen }),
  setCreateFolderOpen: (isOpen) => set({ isCreateFolderOpen: isOpen }),

  // async modals -- connect confirm
  confirmConnect: noop,
  cancelConnect: noop,

  setConnectConfirmOpen: async (title: string) =>
    new Promise((resolve) => {
      set({
        isConnectConfirmOpen: true,
        connectConfirmTitle: title,
        confirmConnect: () => {
          resolve(true);
          set({ isConnectConfirmOpen: false });
        },
        cancelConnect: () => {
          resolve(false);
          set({ isConnectConfirmOpen: false });
        },
      });
    }),

  // async modals -- site picker
  confirmSite: noop,
  cancelSite: noop,

  changeSite: (site) => set({ site }),
  setSitePickerOpen: async () =>
    new Promise((resolve) => {
      set((state) => ({
        site: {
          ...state.site,
          isOpen: true,
        },
        confirmSite: (site: Site) => {
          resolve(site);
          set({ site });
        },
        cancelSite: () => {
          resolve(null);
          set((s) => ({
            site: {
              ...s.site,
              isOpen: false,
            },
          }));
        },
      }));
    }),

  // async modals -- document library picker
  confirmDocumentLibrary: noop,
  cancelDocumentLibrary: noop,

  changeDocumentLibrary: (library) => set({ documentLibrary: library }),
  setDocumentLibraryPickerOpen: async () =>
    new Promise((resolve) => {
      set((state) => ({
        documentLibrary: {
          ...state.documentLibrary,
          isOpen: true,
        },
        confirmDocumentLibrary: (library: DocumentLibrary) => {
          resolve(library);
          set({ documentLibrary: library });
        },
        cancelDocumentLibrary: () => {
          resolve(null);
          set((s) => ({
            documentLibrary: {
              ...s.documentLibrary,
              isOpen: false,
            },
          }));
        },
      }));
    }),

  // async modals -- resync
  confirmResync: noop,
  cancelResync: noop,

  setResyncOpen: async () =>
    new Promise((resolve) => {
      set({
        isResyncOpen: true,
        confirmResync: () => {
          resolve(true);
          set({ isResyncOpen: false });
        },
        cancelResync: () => {
          resolve(false);
          set({ isResyncOpen: false });
        },
      });
    }),
}));
